import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Col, Row, Text, Panel, Message, Loader, Badge } from "rsuite";
import CheckIcon from '@rsuite/icons/Check';
import CloseIcon from '@rsuite/icons/Close';
import PageBase from "../../../components/PageBase";
import Header from "../../../layouts/Header";
import type { Order } from "../../../my-type";
import { getOrder } from "../../../service/OrderService";

export default function ECPayResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState<Order | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const processPaymentResult = async () => {
      try {
        setIsLoading(true);

        // Extract orderId and status from URL search params
        const orderId = searchParams.get("orderId");
        const status = searchParams.get("status");

        // Validate required parameters
        if (!orderId || !status) {
          setErrorMessage("付款資料不完整，請聯絡客服");
          setIsLoading(false);
          return;
        }

        setPaymentStatus(status);

        // Fetch order details using orderId
        const orderData = await getOrder(orderId);

        if (!orderData) {
          setErrorMessage("無法取得訂單資訊，請聯絡客服");
          setIsLoading(false);
          return;
        }

        setOrder(orderData);

        // Handle payment status
        if (status !== "success" && status !== "failed") {
          setErrorMessage("付款狀態無效");
          setIsLoading(false);
          return;
        }

        if (status === "failed") {
          setErrorMessage("付款失敗，請重新嘗試或聯絡客服");
        }

      } catch (error) {
        console.error("Error processing payment result:", error);
        setErrorMessage("處理付款結果時發生錯誤，請聯絡客服");
      } finally {
        setIsLoading(false);
      }
    };

    processPaymentResult();
  }, [searchParams]);

  const getPaymentStatusInfo = () => {
    if (!order) return { type: "error", text: "無法取得付款資訊", icon: <CloseIcon /> };

    if (paymentStatus === "success") {
      return {
        type: "success",
        text: "付款成功",
        icon: <CheckIcon />,
        color: "green"
      };
    } else {
      return {
        type: "error",
        text: "付款失敗",
        icon: <CloseIcon />,
        color: "red"
      };
    }
  };

  const handleBackToOrders = () => {
    navigate('/rider/order');
  };

  const handleViewOrder = () => {
    if (order?.orderID) {
      navigate(`/rider/order/${order.orderID}`);
    } else {
      navigate('/rider/order');
    }
  };

  const header = (
    <Header
      HeaderTitle="付款結果"
      isShowRefresh={false}
      OnReturn={handleBackToOrders}
      OnRefresh={() => { }}
    />
  );

  if (isLoading) {
    return (
      <PageBase
        header={header}
        content={
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Loader size="lg" content="處理付款結果中..." />
          </div>
        }
        showSide
      />
    );
  }

  if (errorMessage) {
    return (
      <PageBase
        header={header}
        content={
          <div className="container">
            <Panel bordered style={{ marginBottom: '20px' }}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <CloseIcon style={{ fontSize: '48px', color: 'red', marginBottom: '20px' }} />
                <h4 style={{ color: 'red', marginBottom: '10px' }}>付款處理失敗</h4>
                <Text muted>{errorMessage}</Text>
              </div>
            </Panel>
            <Row>
              <Col xs={24} style={{ textAlign: 'center' }}>
                <Button
                  color="blue"
                  appearance="primary"
                  onClick={handleBackToOrders}
                  style={{ minWidth: '120px' }}
                >
                  返回訂單列表
                </Button>
              </Col>
            </Row>
          </div>
        }
        showSide
      />
    );
  }

  if (!order) {
    return (
      <PageBase
        header={header}
        content={
          <div className="container">
            <Message type="error" header="錯誤" style={{ marginBottom: '20px' }}>
              無法取得訂單資訊
            </Message>
          </div>
        }
        showSide
      />
    );
  }

  const statusInfo = getPaymentStatusInfo();

  return (
    <PageBase
      header={header}
      content={
        <div className="container">
          {/* Payment Status Panel */}
          <Panel bordered style={{ marginBottom: '20px' }}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ fontSize: '48px', color: statusInfo.color, marginBottom: '20px' }}>
                {statusInfo.icon}
              </div>
              <h4 style={{ color: statusInfo.color, marginBottom: '10px' }}>
                {statusInfo.text}
              </h4>
              {paymentStatus === "failed" && errorMessage && (
                <Text muted>{errorMessage}</Text>
              )}
            </div>
          </Panel>

          {/* Order Details Panel */}
          <Panel header="訂單詳情" bordered style={{ marginBottom: '20px' }}>
            <Row style={{ marginBottom: '15px' }}>
              <Col xs={8}>
                <Text weight="bold">訂單編號：</Text>
              </Col>
              <Col xs={16}>
                <Text muted>{order.orderID}</Text>
              </Col>
            </Row>

            <Row style={{ marginBottom: '15px' }}>
              <Col xs={8}>
                <Text weight="bold">乘客姓名：</Text>
              </Col>
              <Col xs={16}>
                <Text muted>{order.passengerName}</Text>
              </Col>
            </Row>

            <Row style={{ marginBottom: '15px' }}>
              <Col xs={8}>
                <Text weight="bold">乘車費用：</Text>
              </Col>
              <Col xs={16}>
                <Text muted>NT$ {order.rideFee.toLocaleString()}</Text>
              </Col>
            </Row>

            <Row style={{ marginBottom: '15px' }}>
              <Col xs={8}>
                <Text weight="bold">乘車時間：</Text>
              </Col>
              <Col xs={16}>
                <Text muted>{new Date(order.rideTime).toLocaleString()}</Text>
              </Col>
            </Row>

            <Row style={{ marginBottom: '15px' }}>
              <Col xs={8}>
                <Text weight="bold">目的地：</Text>
              </Col>
              <Col xs={16}>
                <Text muted>{order.address}</Text>
              </Col>
            </Row>

            {order.flightNo && (
              <Row style={{ marginBottom: '15px' }}>
                <Col xs={8}>
                  <Text weight="bold">航班號碼：</Text>
                </Col>
                <Col xs={16}>
                  <Text muted>{order.flightNo}</Text>
                </Col>
              </Row>
            )}

            <Row style={{ marginBottom: '15px' }}>
              <Col xs={8}>
                <Text weight="bold">付款狀態：</Text>
              </Col>
              <Col xs={16}>
                <Badge
                  content={paymentStatus === "success" ? "付款成功" : "付款失敗"}
                  color={paymentStatus === "success" ? "green" : "red"}
                />
              </Col>
            </Row>
          </Panel>

          {/* Action Buttons */}
          <Row gutter={16}>
            <Col xs={12}>
              <Button
                color="blue"
                appearance="primary"
                onClick={handleViewOrder}
                style={{ width: '100%' }}
                disabled={!order?.orderID}
              >
                查看訂單
              </Button>
            </Col>
            <Col xs={12}>
              <Button
                appearance="default"
                onClick={handleBackToOrders}
                style={{ width: '100%' }}
              >
                返回訂單列表
              </Button>
            </Col>
          </Row>

          {/* Additional Information */}
          {paymentStatus === "failed" && (
            <Message type="warning" header="注意" style={{ marginTop: '20px' }}>
              付款失敗，請檢查付款資訊或聯絡客服。
            </Message>
          )}
        </div>
      }
      showSide
    />
  );
}
